import { z } from 'zod';
import {
  dataHoraSchema,
  dataSchema,
  dinheiroSchema,
  percentualSchema,
  politicaPedidoExcedenteSchema,
  uuidSchema,
} from './comum.ts';
import type { GrupoContaContabil } from './financeiro.ts';
import { MOTOR_NFSE_PADRAO, motorNfseSchema } from './motores-nfse.ts';
import {
  POLITICA_FATURAMENTO_EXCEDENTE_PADRAO,
  politicaFaturamentoExcedenteSchema,
} from './saldo-a-faturar.ts';

/**
 * Configuração da conta contratante.
 *
 * Reúne duas tabelas que o usuário enxerga como uma coisa só: `tenants`
 * (identificação da conta) e `configuracao_conta` (parâmetros de operação).
 *
 * **Não existe id nesta rota.** A conta é sempre a do `tid` do token — aceitar
 * um id do cliente transformaria a tela num caminho para editar a conta de
 * outro contratante, já que `tenants` é tabela global e não passa por RLS.
 */

/**
 * Referência opcional a um cadastro, como o formulário a manda.
 *
 * O `<select>` vazio devolve `''`, e `''` não é um uuid — sem esta conversão
 * toda limpeza de campo viraria erro de validação em vez de "não configurado".
 */
const referenciaOpcionalSchema = z
  .union([uuidSchema, z.literal(''), z.null()])
  .transform((valor) => (valor === '' || valor === null ? null : valor))
  .prefault(null);

/**
 * Parâmetros das movimentações acessórias da baixa (`.spec/010`, "Movimentação
 * de conta corrente": *"os históricos financeiro e bancário usados nas
 * movimentações de juros, outros e desconto são parâmetros da Configuração da
 * Conta"*).
 *
 * **A conta contábil entra junto, e a spec não a lista.** A divergência é
 * deliberada: `movimentacoes_conta_corrente.conta_contabil_id` é `NOT NULL`, a
 * spec não oferece outra fonte para as três naturezas acessórias, e a
 * alternativa seria o service escolher uma conta por conta própria. Parametrizar
 * ao lado dos históricos é o que a spec faria se tivesse chegado até aqui — e é
 * o que permite separar juros pagos de acessórias diversas no fechamento, que é
 * a razão de `JUROS` e `OUTROS` serem naturezas diferentes.
 *
 * **O histórico financeiro não é parâmetro.** Ele é a *forma de pagamento*, e a
 * baixa já escolhe uma: as movimentações acessórias herdam a mesma do
 * principal, porque o juro que entrou junto com o pix entrou pelo pix. Cada
 * natureza teve um parâmetro próprio, e ele só produzia duas maneiras de a
 * mesma baixa dizer como o dinheiro andou — uma no cabeçalho, outra na
 * configuração, e nada obrigando as duas a concordarem. O que distingue as
 * acessórias no extrato é o histórico **bancário**, e ele continua aqui.
 *
 * **São doze campos, e não seis: pagar e receber não compartilham nenhum.**
 * O juro que a empresa paga é despesa; o que ela recebe é receita, e a conta
 * contábil de uma nunca serve para a outra. O histórico bancário também separa
 * as duas pontas no extrato. Enquanto o parâmetro era único, a única saída era
 * classificar juro recebido na conta de juro pago — o balancete fechava e o
 * resultado do exercício não significava nada.
 */
export const parametrosDaBaixaSchema = z.object({
  historicoBancarioJurosPagarId: referenciaOpcionalSchema,
  contaContabilJurosPagarId: referenciaOpcionalSchema,
  historicoBancarioOutrosPagarId: referenciaOpcionalSchema,
  contaContabilOutrosPagarId: referenciaOpcionalSchema,
  historicoBancarioDescontoPagarId: referenciaOpcionalSchema,
  contaContabilDescontoPagarId: referenciaOpcionalSchema,

  historicoBancarioJurosReceberId: referenciaOpcionalSchema,
  contaContabilJurosReceberId: referenciaOpcionalSchema,
  historicoBancarioOutrosReceberId: referenciaOpcionalSchema,
  contaContabilOutrosReceberId: referenciaOpcionalSchema,
  historicoBancarioDescontoReceberId: referenciaOpcionalSchema,
  contaContabilDescontoReceberId: referenciaOpcionalSchema,
});

export type ParametrosDaBaixa = z.infer<typeof parametrosDaBaixaSchema>;

/** Os dois processos de baixa. É o eixo que separa os parâmetros. */
export const TIPOS_DE_TITULO = ['PAGAR', 'RECEBER'] as const;

export type TipoDeTitulo = (typeof TIPOS_DE_TITULO)[number];

export const ROTULO_TIPO_DE_TITULO: Readonly<Record<TipoDeTitulo, string>> = {
  PAGAR: 'Contas a pagar',
  RECEBER: 'Contas a receber',
};

/**
 * Os grupos do plano que a **classificação do título** aceita.
 *
 * Custo ou despesa no que se paga; receita no que se recebe. É a mesma regra de
 * `GRUPOS_CONTA_DO_CONTRATO`, no mesmo plano de contas, e por isso o par de
 * valores é idêntico — o eixo é que muda: lá é o tipo do contrato, aqui é o
 * processo do título.
 *
 * São **dois** grupos do lado do pagar porque o plano separa o que a obra
 * consome do que a administração gasta: a nota de material é custo, a conta de
 * telefone é despesa. Escolher um só jogaria metade dos títulos na conta errada
 * do DRE.
 *
 * Ativo e passivo ficam de fora dos dois lados. Classificar um título a pagar
 * em "Fornecedores" é a contrapartida do lançamento, não a natureza do gasto —
 * e é ela que o fechamento procura.
 */
export const GRUPOS_CONTA_DO_TITULO = {
  PAGAR: ['CUSTO', 'DESPESA'],
  RECEBER: ['RECEITA'],
} as const satisfies Readonly<Record<TipoDeTitulo, readonly GrupoContaContabil[]>>;

interface NaturezaAcessoria {
  natureza: 'JUROS' | 'OUTROS' | 'DESCONTO';
  rotulo: string;
  campoValor: 'valorJuros' | 'valorOutros' | 'valorDesconto';
  /**
   * Não há `historicoFinanceiro` aqui: a acessória usa o da própria baixa. Ver
   * `parametrosDaBaixaSchema`.
   */
  historicoBancario: keyof ParametrosDaBaixa;
  contaContabil: keyof ParametrosDaBaixa;
}

/**
 * As três naturezas acessórias de cada processo, e os parâmetros de cada uma.
 *
 * Indexado por tipo de título, e não uma lista só: a baixa do contas a pagar lê
 * `NATUREZAS_ACESSORIAS.PAGAR`, a do contas a receber lê `.RECEBER`, e nenhuma
 * das duas tem como alcançar o parâmetro da outra por engano — que era
 * exatamente o que acontecia quando os nove campos eram compartilhados.
 *
 * Continua sendo estrutura de dados, e não `if` espalhado: o service que monta a
 * movimentação e a tela que desenha os campos percorrem a **mesma** lista, e
 * acrescentar uma natureza nova é uma entrada em cada tipo, não uma caçada.
 */
export const NATUREZAS_ACESSORIAS = {
  PAGAR: [
    {
      natureza: 'JUROS',
      rotulo: 'Juros',
      campoValor: 'valorJuros',
      historicoBancario: 'historicoBancarioJurosPagarId',
      contaContabil: 'contaContabilJurosPagarId',
    },
    {
      natureza: 'OUTROS',
      rotulo: 'Outros valores',
      campoValor: 'valorOutros',
      historicoBancario: 'historicoBancarioOutrosPagarId',
      contaContabil: 'contaContabilOutrosPagarId',
    },
    {
      natureza: 'DESCONTO',
      rotulo: 'Desconto',
      campoValor: 'valorDesconto',
      historicoBancario: 'historicoBancarioDescontoPagarId',
      contaContabil: 'contaContabilDescontoPagarId',
    },
  ],
  RECEBER: [
    {
      natureza: 'JUROS',
      rotulo: 'Juros',
      campoValor: 'valorJuros',
      historicoBancario: 'historicoBancarioJurosReceberId',
      contaContabil: 'contaContabilJurosReceberId',
    },
    {
      natureza: 'OUTROS',
      rotulo: 'Outros valores',
      campoValor: 'valorOutros',
      historicoBancario: 'historicoBancarioOutrosReceberId',
      contaContabil: 'contaContabilOutrosReceberId',
    },
    {
      natureza: 'DESCONTO',
      rotulo: 'Desconto',
      campoValor: 'valorDesconto',
      historicoBancario: 'historicoBancarioDescontoReceberId',
      contaContabil: 'contaContabilDescontoReceberId',
    },
  ],
} as const satisfies Record<TipoDeTitulo, ReadonlyArray<NaturezaAcessoria>>;

/**
 * Os seis campos de um processo, na ordem em que a tela os desenha.
 *
 * Derivado de `NATUREZAS_ACESSORIAS`, e não uma segunda lista escrita à mão: a
 * cópia que ficasse para trás deixaria um campo fora do formulário, e ele seria
 * apagado no salvamento seguinte sem nada indicar o motivo.
 */
export function camposDaBaixa(tipo: TipoDeTitulo): (keyof ParametrosDaBaixa)[] {
  return NATUREZAS_ACESSORIAS[tipo].flatMap((n) => [n.historicoBancario, n.contaContabil]);
}

/** Os doze campos, na ordem pagar e depois receber. */
export const CAMPOS_DA_BAIXA: readonly (keyof ParametrosDaBaixa)[] =
  TIPOS_DE_TITULO.flatMap(camposDaBaixa);

/* -------------------------------------------------------------------------- */
/* Transferência entre contas correntes                                       */
/* -------------------------------------------------------------------------- */

/**
 * Parâmetros da transferência entre contas correntes.
 *
 * **Os dois campos são parâmetro pela mesma razão que os das acessórias da
 * baixa**: a movimentação de conta corrente exige `historico_bancario_id` e
 * `conta_contabil_id` como `NOT NULL`, e a transferência não tem de onde
 * tirá-los. As alternativas eram afrouxar as duas colunas do livro-razão para
 * todas as origens ou o service escolher por conta própria; parametrizar é o que
 * deixa a escolha registrada e visível a quem administra a conta.
 *
 * **Por que não se perguntam no formulário.** Na baixa, a conta contábil de cada
 * natureza varia com o que se paga. Aqui os dois valem para **toda**
 * transferência: transferir é sempre a mesma natureza de movimento e sempre a
 * mesma contrapartida de balanço. Perguntá-los a cada documento produziria
 * extratos em que a mesma operação aparece com três nomes diferentes, conforme
 * quem digitou.
 *
 * **A conta contábil é de balanço** — ativo, tipicamente bancos ou uma conta de
 * transferências entre contas —, e é por isso que a transferência não passa pelo
 * lançamento manual, que só aceita conta de resultado
 * (`GRUPOS_POR_TIPO_MOVIMENTO`, em `lancamentos.ts`). As **duas pernas usam a
 * mesma conta**: o débito da origem e o crédito do destino se anulam nela, e é
 * isso que faz a transferência não formar resultado nenhum. Uma conta por ponta
 * deixaria saldo residual nas duas, visível só no fechamento.
 *
 * **O histórico financeiro não é parâmetro**, pela mesma razão de não ser nas
 * acessórias da baixa: ele é a *forma de pagamento*, e ela muda por documento —
 * a mesma conta manda dinheiro por pix hoje e por TED amanhã. Quem o informa é
 * quem está com a transferência na mão.
 */
export const parametrosDaTransferenciaSchema = z.object({
  historicoBancarioTransferenciaId: referenciaOpcionalSchema,
  contaContabilTransferenciaId: referenciaOpcionalSchema,
});

export type ParametrosDaTransferencia = z.infer<typeof parametrosDaTransferenciaSchema>;

/** Os dois campos da transferência, na ordem em que a tela os desenha. */
export const CAMPOS_DA_TRANSFERENCIA: readonly (keyof ParametrosDaTransferencia)[] = [
  'historicoBancarioTransferenciaId',
  'contaContabilTransferenciaId',
];

/**
 * Todos os parâmetros que apontam para um cadastro: os doze das acessórias mais
 * os dois da transferência.
 *
 * Existe porque o repositório da conta e o formulário da tela tratam **todos** da
 * mesma forma — `''` na tela vira `null` no payload, ausente no banco vira `''`
 * de volta —, e os dois percorrem esta lista em laço em vez de escreverem
 * catorze linhas cada. O parâmetro novo que entrar na lista entra nos dois lados
 * de uma vez; escritos à mão, ele ficaria em branco ao reabrir a tela e seria
 * apagado no salvamento seguinte, sem nada indicando o motivo.
 *
 * `CAMPOS_DA_BAIXA` continua separado porque a **seção do formulário** das
 * acessórias é desenhada só a partir dele: misturar os dois ali faria a
 * transferência aparecer como se fosse uma quarta natureza de baixa.
 */
export type ParametrosDeReferencia = ParametrosDaBaixa & ParametrosDaTransferencia;

export const CAMPOS_DE_REFERENCIA: readonly (keyof ParametrosDeReferencia)[] = [
  ...CAMPOS_DA_BAIXA,
  ...CAMPOS_DA_TRANSFERENCIA,
];

/* -------------------------------------------------------------------------- */

/**
 * A caixa em que os cadastros de texto livre são gravados (`.spec/015`, decisão 56).
 *
 * Os dois valores são os mesmos que o botão `Aa` já oferece campo a campo, e é
 * de propósito: o parâmetro não inventa uma terceira forma, ele escolhe qual das
 * duas a conta usa por padrão. Capitalizar cada palavra continua fora — "Cimento
 * Portland Csn Cp Iii" transforma a sigla em palavra, e é a sigla que distingue
 * dois materiais parecidos.
 *
 * **Sem um terceiro valor "não aplicar".** Ele foi avaliado e recusado: o botão
 * `Aa` continua em cada campo, então quem quiser a grafia que digitou a
 * restabelece com um clique — e um parâmetro de três estados obrigaria toda tela
 * a distinguir "não escolheu" de "escolheu não aplicar", que é a mesma coisa
 * vista do lado de quem digita.
 *
 * `ALTA` e `FRASE` repetem os nomes que `caixaDoTexto` já usava no frontend, em
 * vez de `CAIXA_ALTA` / `PRIMEIRA_MAIUSCULA`: um vocabulário só para a mesma
 * ideia, e o `z.infer` daqui passou a ser o tipo de lá.
 */
export const caixaDeTextoSchema = z.enum(['ALTA', 'FRASE']);

export type CaixaDeTexto = z.infer<typeof caixaDeTextoSchema>;

export const ROTULO_CAIXA_DE_TEXTO: Readonly<Record<CaixaDeTexto, string>> = {
  ALTA: 'MAIÚSCULAS',
  FRASE: 'Primeira maiúscula',
};

/**
 * O que cada forma faz, na palavra do usuário.
 *
 * Como `DESCRICAO_POLITICA_EXCEDENTE`, **estes textos são promessa**: quem
 * escolhe está decidindo pelo que está escrito aqui. A promessa inclui o que
 * acontece ao abrir um cadastro antigo — o texto aparece ajustado, e só fica
 * gravado assim se a pessoa salvar.
 */
export const DESCRICAO_CAIXA_DE_TEXTO: Readonly<Record<CaixaDeTexto, string>> = {
  ALTA: 'RAZÃO SOCIAL, NOME FANTASIA, NOME DO INSUMO E ENDEREÇO EM CAIXA ALTA. É como a Receita e o XML do fornecedor devolvem o texto.',
  FRASE:
    'Nome de pessoa, razão social e endereço ganham maiúscula em cada palavra, com as partículas em minúscula — "Adriano Luiz de Souza", "Rua das Acácias". O nome do insumo fica só com a primeira letra, porque ali capitalizar cada palavra transforma a sigla em palavra. As siglas societárias (LTDA, ME, S/A) continuam em caixa alta.',
};

/**
 * O que toda tela de cadastro precisa saber da conta.
 *
 * Recorte próprio, e não a `Conta` inteira: a configuração completa é
 * `admin.conta.ver`, e quem cadastra insumo não tem essa permissão — nem deve
 * ter, porque ali estão as contas contábeis das acessórias e a trava de
 * fechamento. A caixa do texto é preferência de digitação, e a rota que a
 * devolve pede só `autenticado`.
 */
export const preferenciasDaContaSchema = z.object({
  caixaDeTexto: caixaDeTextoSchema,
});

export type PreferenciasDaConta = z.infer<typeof preferenciasDaContaSchema>;

/* -------------------------------------------------------------------------- */

export const parametrosContaSchema = parametrosDaBaixaSchema.extend({
  ...parametrosDaTransferenciaSchema.shape,
  politicaPedidoExcedente: politicaPedidoExcedenteSchema,
  /**
   * Só é consultado na política TOLERANCIA, mas é gravado sempre: zerar o
   * campo ao trocar de política apagaria o valor que o usuário ajustou, e ele
   * teria de redigitá-lo ao voltar atrás.
   */
  percentualToleranciaPadrao: percentualSchema,
  /** Nulo desativa a regra de duplo aval por valor (.spec/005, seção 3). */
  valorLimiteSegundaAprovacao: dinheiroSchema.nullable(),
  /**
   * O piso das datas de lançamento (`.spec/015`, decisão 34).
   *
   * Nenhum documento se lança com data anterior a ela. **Nula desliga a trava**,
   * e é o padrão: conta nova não tem passado a proteger.
   *
   * É o par do teto que vive em `dataDoFatoSchema`, e a diferença entre os dois
   * é o motivo de um ser parâmetro e o outro não. O teto — "não é futura" — não
   * é escolha de ninguém: documento que registra o que já aconteceu com data de
   * amanhã é contradição. O piso é política: cada conta fecha o mês quando quer,
   * e importar histórico é evento legítimo. Por isso ele se recua, e o outro
   * não se desliga.
   *
   * **Sem teto próprio**: pôr a data de bloqueio no futuro congela a conta
   * inteira, e isso é uma escolha legítima de quem quer parar tudo. Recusá-la
   * aqui seria decidir pelo administrador o que ele pode querer.
   *
   * `.prefault(null)` como os doze parâmetros das acessórias, e pela mesma razão:
   * ausente é "não configurado", não é erro. Sem ele, um parâmetro novo recusaria
   * todo payload gravado antes de ele existir — e a tela de configuração é
   * justamente onde não se pode travar a gravação por um campo que o cliente
   * ainda não viu.
   */
  dataBloqueioLancamentos: dataSchema.nullable().prefault(null),
  /**
   * Cadastrar uma obra cria o depósito do canteiro dela? (`.spec/015`, decisão 42.)
   *
   * **Ligado é o padrão, e continua sendo a recomendação** — é a decisão 11
   * inteira: movimentar estoque exige indicar um depósito, e a obra sem local
   * trava a primeira entrega pedindo um cadastro que o usuário não sabe que
   * precisa fazer. O parâmetro não revoga aquele argumento; ele reconhece que o
   * argumento pressupõe uma conta que **controla estoque por local**.
   *
   * A conta que não controla lança tudo no Estoque Geral da empresa, e para ela
   * um depósito por obra não é proteção: é um item a mais em todo seletor de
   * movimentação, com o nome de uma obra, para nunca receber nada. Numa
   * construtora com quarenta obras encerradas, o seletor de depósito passa a ter
   * quarenta linhas mortas na frente da única que se usa.
   *
   * **Só governa o nascimento.** Desligar não apaga nem inativa canteiro
   * nenhum: o que já existe continua com saldo, vínculo e histórico, continua
   * acompanhando a obra ao trocar de empresa e continua sendo inativado junto
   * com ela. Ligar de volta também não cria canteiro para as obras que passaram
   * sem — criar é evento do cadastro, e reconstituí-lo para trás inventaria
   * depósitos que ninguém pediu, cada um disputando nome com os que já existem.
   *
   * `.prefault(true)` pela mesma razão de `dataBloqueioLancamentos` ter o dele:
   * ausente no payload é "conta que ainda não viu este campo", não erro — e a
   * tela de configuração é justamente onde não se pode travar a gravação por um
   * parâmetro que o cliente ainda não viu.
   */
  criarDepositoDaObra: z.boolean().prefault(true),
  /**
   * A caixa dos cadastros de texto livre (`.spec/015`, decisão 56).
   *
   * Vale para os seis campos que já tinham o botão `Aa`: os dois nomes da
   * pessoa, o nome do insumo e os três campos livres do endereço. Não vale para
   * código, documento nem para o que vem de um cadastro escolhido em seletor —
   * ali o texto não é digitado, é referência.
   *
   * `.prefault('FRASE')` pela mesma razão dos vizinhos: ausente no payload é
   * "conta que ainda não viu este campo", não erro. E `FRASE` é o padrão porque
   * é o que o sistema já recomendava em texto — a caixa alta não é convenção em
   * logradouro nem em nome de material, e "AV PRES VARGAS" foi justamente o
   * exemplo que fechou a decisão de não adivinhar sigla pela grafia.
   */
  caixaDeTexto: caixaDeTextoSchema.prefault('FRASE'),
  /**
   * Segregação de funções na baixa (`.spec/005`, seção 3): quem lançou o
   * título não o baixa. `.prefault(false)` — desligada até o administrador
   * ligar, e pelo mesmo motivo dos vizinhos: ausente no payload é conta que
   * ainda não viu o campo.
   */
  segregarBaixaDeTitulo: z.boolean().prefault(false),
  /**
   * O motor que emite a nota fiscal de serviço desta conta (`.spec/015`,
   * decisão 89) — ver `motores-nfse.ts`.
   *
   * Da **conta**, e não da empresa: é uma escolha de fornecedor de serviço, e a
   * conta contrata um. O que varia por empresa — inscrição municipal, ambiente,
   * série — está no cadastro dela (`empresas-fiscal.ts`).
   *
   * `.prefault()` como os vizinhos: ausente no payload é conta que ainda não viu
   * o campo, e ela emite pelo Manual, que não depende de configuração nenhuma.
   */
  motorNfse: motorNfseSchema.prefault(MOTOR_NFSE_PADRAO),
  /**
   * O que a emissão faz com a nota de serviço que passa do saldo a faturar da
   * obra ou do contrato (`.spec/015`, decisão 94) — ver `saldo-a-faturar.ts`.
   *
   * Parâmetro, e não regra fixa, por resposta do Adriano: há conta que só
   * fatura acima do contrato depois de aditar, e conta que emite e acerta o
   * cadastro depois. É a mesma pergunta de `politicaPedidoExcedente`, do outro
   * lado do balcão.
   *
   * `.prefault()` como os vizinhos: ausente no payload é conta que ainda não viu
   * o campo, e ela bloqueia.
   */
  politicaFaturamentoExcedente: politicaFaturamentoExcedenteSchema.prefault(
    POLITICA_FATURAMENTO_EXCEDENTE_PADRAO,
  ),
  /**
   * Autorização dos documentos de compra (`.spec/015`, decisão 108) — uma
   * caixa por documento, porque há conta que só quer autorizar o pedido, que é
   * o que vai ao fornecedor.
   *
   * Ligada, o documento só passa adiante aprovado: a lista não gera pedido, a
   * solicitação não é origem de pedido, o pedido não sai em PDF nem recebe
   * nota. Ver `aprovacao-compra.ts`.
   *
   * `.prefault(false)` como os vizinhos: ausente no payload é conta que ainda
   * não viu o campo, e ela continua sem autorização.
   */
  aprovarListaCompraDireta: z.boolean().prefault(false),
  aprovarSolicitacaoCompra: z.boolean().prefault(false),
  aprovarPedidoCompra: z.boolean().prefault(false),
});

export const atualizarContaSchema = parametrosContaSchema
  .extend({
    nome: z.string().trim().min(2, 'Informe a razão social da conta.').max(200),
  })
  .superRefine((dados, ctx) => {
    if (dados.politicaPedidoExcedente !== 'TOLERANCIA') return;

    // Tolerância de 0% com política TOLERANCIA é o mesmo que BLOQUEAR, escrito
    // de um jeito que ninguém entende ao ler a configuração seis meses depois.
    if (Number(dados.percentualToleranciaPadrao) > 0) return;

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['percentualToleranciaPadrao'],
      message: 'Com a política de tolerância, informe um percentual maior que zero.',
    });
  });

export type AtualizarConta = z.infer<typeof atualizarContaSchema>;

export const contaSchema = parametrosContaSchema.extend({
  id: uuidSchema,
  nome: z.string(),
  /**
   * Somente leitura na resposta.
   *
   * Suspender a conta é decisão comercial de quem opera o sistema, não botão
   * de autoatendimento: um administrador que desmarcasse isto tiraria a
   * própria empresa inteira do ar, e nem ele conseguiria entrar para desfazer.
   */
  ativo: z.boolean(),
  atualizadoEm: dataHoraSchema,
});

export type Conta = z.infer<typeof contaSchema>;

export const ROTULO_POLITICA_EXCEDENTE: Readonly<
  Record<z.infer<typeof politicaPedidoExcedenteSchema>, string>
> = {
  BLOQUEAR: 'Bloquear',
  ALERTAR: 'Alertar e permitir',
  TOLERANCIA: 'Permitir até a tolerância',
};

/**
 * O que cada política faz, na palavra do usuário.
 *
 * **Estes textos são promessa**, e é assim que se leem na tela de configuração:
 * quem escolhe uma das três está decidindo pelo que está escrito aqui. O "e o
 * excedente fica registrado" da opção `ALERTAR` passou meses sem lastro — o
 * aviso morria no repository e o excesso sumia no teto da quantidade atendida —,
 * e mudar a frase para descrever o que o código fazia teria sido o conserto
 * errado: a `.spec/010` manda avisar. Ao mexer aqui, mexa no comportamento
 * primeiro.
 *
 * As duas metades da promessa moram em lugares diferentes: o aviso vem na
 * resposta da gravação (`avisoDoPedidoSchema`, em `pedidos-compra.ts`) e o
 * registro fica em `solicitacoes_compra_itens.quantidade_excedente`, derivado
 * junto com a quantidade atendida.
 */
export const DESCRICAO_POLITICA_EXCEDENTE: Readonly<
  Record<z.infer<typeof politicaPedidoExcedenteSchema>, string>
> = {
  BLOQUEAR: 'O pedido é recusado quando a quantidade passa da solicitada.',
  ALERTAR:
    'O pedido é aceito com aviso na gravação, e o excedente fica registrado no item da solicitação.',
  TOLERANCIA:
    'O pedido é aceito até o percentual configurado. Acima dele, é recusado. O percentual do insumo, quando existir, prevalece sobre este.',
};
